import Debounce from '../helpers/Debounce';

const ALPHA_CURSOR = 1;
const ALPHA_RANDOM = 0.75;

const COLOR_DARK = '#211a1e';
const COLOR_GREEN = '#216247';
const COLOR_WHITE = '#ffffff';
const COLS_DESKTOP = 16;
const COLS_MOBILE = 5;
const ROWS = 20; //5;

const SPAWN_SPEED = 500;


export default class BackgroundGrid {

	constructor(element) {

		// do not initialize this component if the user prefers reduced motion
		const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
		if (!mediaQuery || mediaQuery.matches) {
			return;
		}

		const attrs = element.getAttribute('data-bg-grid').split('|');
		const attrType = attrs[0];
		const attrColor = attrs[1];

		this.color = (attrColor === 'dark') ? COLOR_DARK : COLOR_GREEN;
		this.color = (attrColor === 'white') ? COLOR_WHITE : this.color;
		this.element = element;
		this.type = attrType;
		this.canvas = document.createElement('canvas');
		this.context = this.canvas.getContext('2d');
		this.frameAnim = null;
		this.interval = null;
		this.cols = 0;
		this.rows = 0;
		this.grid = [];
		this.height = 0;
		this.offset = 0;
		this.square = 0;
		this.width = 0;

		this.inViewport = this.inViewport.bind(this);
		this.mouseMove = this.mouseMove.bind(this);
		this.render = this.render.bind(this);
		this.resize = this.resize.bind(this);
		this.resizeDebounce = Debounce(this.resize, 250);
		this.spawn = this.spawn.bind(this);

		this.element.appendChild(this.canvas);

		const inViewportObserver = new window.IntersectionObserver(
			this.inViewport,
			{
				root: null,
				threshold: 0.0
			}
		);
		inViewportObserver.observe(this.element);

	}

	disable() {
		cancelAnimationFrame(this.frameAnim);
		window.removeEventListener('orientationchange', this.resizeDebounce);
		window.removeEventListener('resize', this.resizeDebounce);
		this.element.removeEventListener('mousemove', this.mouseMove);
		clearInterval(this.interval);
	}

	enable() {
		this.disable();
		window.addEventListener('orientationchange', this.resizeDebounce, {capture: true, passive: true});
		window.addEventListener('resize', this.resizeDebounce, {capture: true, passive: true});
		this.element.addEventListener('mousemove', this.mouseMove);
		this.interval = setInterval(this.spawn, SPAWN_SPEED);
		this.resize();
		this.render();
	}

	inViewport(entries) {
		const entry = entries[0];
		if (entry.isIntersecting) {
			this.enable();
		} else {
			this.disable();
		}
	}

	mouseMove(event) {
		const offsets = this.element.getBoundingClientRect();
		const x = Math.floor((event.clientX - offsets.left) / this.square);
		const y = Math.floor((event.clientY - offsets.top - this.offset) / this.square);
		const index = Math.floor((y * this.cols) + x);
		let alpha = ALPHA_CURSOR;
		if (this.type === 'bottom') {
			alpha = (y < this.rows - ROWS) ? 0 : alpha;
		} else if (this.type === 'top') {
			alpha = (y >= ROWS) ? 0 : alpha;
		}
		if (index < this.grid.length) {
			this.grid[index] = alpha;
		}
	}

	render() {

		// vars
		let i, l, x, y;

		// fade out squares over time
		for (i=0, l=this.grid.length; i<l; i++) {
			this.grid[i] = Math.max(0, this.grid[i] * 0.99);
		}

		// clear canvas & draw squares
		this.context.clearRect(0, 0, this.width, this.height);
		this.context.fillStyle = this.color;
		for (i=0, l=this.grid.length; i<l; i++) {
			y = Math.floor(i / this.cols);
			x = i - Math.floor(y * this.cols);
			this.context.globalAlpha = this.grid[i];
			this.context.fillRect(
				x * this.square,
				this.offset + (y * this.square),
				this.square,
				this.square
			);
		}
		this.context.globalAlpha = 1.0;

		this.frameAnim = requestAnimationFrame(this.render);
	}

	resize() {

		// update width & height
		this.width = this.element.offsetWidth;
		this.height = this.element.offsetHeight;
		this.canvas.width = this.width;
		this.canvas.height = this.height;

		// update square size
		if (window.innerWidth >= 768) {
			this.square = this.width / COLS_DESKTOP;
		} else {
			this.square = this.width / COLS_MOBILE;
		}

		// update grid
		this.cols = Math.floor(this.width / this.square);
		this.rows = Math.floor(this.height / this.square);
		const cells = this.cols * this.rows;
		if (this.grid.length !== cells) {
			this.grid = [];
			for (let i=0, l=cells; i<l; i++) {
				this.grid.push(0);
			}
		}

		// update offset
		this.offset = 0;
		if (this.type === 'bottom') {
			this.offset = this.height - (this.rows * this.square);
		} else if (this.type === 'center') {
			this.offset = (this.height - (this.rows * this.square)) * 0.5;
		}

	}

	spawn() {
		let offset = 0;
		if (this.type === 'bottom') {
			offset = this.rows - ROWS;
		} else if (this.type === 'center') {
			offset = Math.floor((this.rows - ROWS) * 0.5);
		}

		const x = Math.floor(Math.random() * this.cols);
		const y = Math.floor(Math.random() * ROWS) + offset;

		const index = Math.floor((y * this.cols) + x);
		if (index < this.grid.length) {
			this.grid[index] = ALPHA_RANDOM;
		}
	}

}

